import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  Users,
  Calendar,
  Award,
  PlayCircle,
  ChevronRight,
  Zap,
  Globe,
  Star,
  Clock,
  MapPin,
  Filter
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

export default function Home2Page() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { theme } = useTheme();
  const [activeFilter, setActiveFilter] = useState('all');

  const featuredMatches = [
    {
      id: 1,
      sport: 'Basketball',
      teams: ['Lakers', 'Warriors'],
      score: [105, 98],
      status: 'Live',
      time: '3rd Quarter',
      viewers: '12.5K',
      emoji: '🏀'
    },
    {
      id: 2,
      sport: 'Volleyball',
      teams: ['Spikes', 'Aces'],
      score: [2, 1],
      status: 'Live',
      time: 'Set 4',
      viewers: '8.3K',
      emoji: '🏐'
    },
    {
      id: 3,
      sport: 'Pickleball',
      teams: ['Paddles', 'Smash'],
      score: [15, 12],
      status: 'Upcoming',
      time: 'In 30 min',
      viewers: '5.2K',
      emoji: '🎾'
    }
  ];

  const leaderboard = [
    { rank: 1, name: 'Alex Rodriguez', sport: 'Basketball', points: 2450, trend: '+12' },
    { rank: 2, name: 'Sarah Johnson', sport: 'Volleyball', points: 2380, trend: '+8' },
    { rank: 3, name: 'Mike Chen', sport: 'Pickleball', points: 2350, trend: '+5' },
    { rank: 4, name: 'Emily Davis', sport: 'Basketball', points: 2320, trend: '-2' },
    { rank: 5, name: 'Tom Wilson', sport: 'Volleyball', points: 2290, trend: '+15' }
  ];

  const Trophy = Award;

  const quickStats = [
    { label: 'Active Games', value: '847', change: '+12%', icon: PlayCircle, color: 'text-green-500' },
    { label: 'Online Players', value: '15.2K', change: '+5%', icon: Users, color: 'text-blue-500' },
    { label: 'Tournaments Today', value: '23', change: '+8%', icon: Trophy, color: 'text-yellow-500' },
    { label: 'Global Ranking', value: '#1', change: 'Maintained', icon: Globe, color: 'text-purple-500' }
  ];

  const upcomingEvents = [
    {
      id: 1,
      title: 'City Championship Finals',
      sport: 'Basketball',
      date: 'Tomorrow',
      time: '7:00 PM',
      location: 'Sports Arena',
      participants: 156,
      prize: '$5,000'
    },
    {
      id: 2,
      title: 'Beach Volleyball Tournament',
      sport: 'Volleyball',
      date: 'This Weekend',
      time: '9:00 AM',
      location: 'Sunset Beach',
      participants: 64,
      prize: '$2,500'
    },
    {
      id: 3,
      title: 'Pickleball Masters Cup',
      sport: 'Pickleball',
      date: 'Next Week',
      time: '6:00 PM',
      location: 'Community Center',
      participants: 32,
      prize: '$1,500'
    }
  ];

  return (
    <div className={`min-h-screen ${theme.background}`}>
      <main className="px-6 py-6">
        <div className="max-w-7xl mx-auto">

          {/* Header Section */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className={`text-4xl lg:text-5xl font-bold ${theme.text} mb-2`}>
                Sports Central
              </h1>
              <p className={`text-lg ${theme.textSecondary}`}>
                {isAuthenticated
                  ? `Welcome back, ${user?.name?.split(' ')[0] || user?.firstName || 'Champion'}! Your sports world awaits.`
                  : 'Live scores, tournaments, and community action in one place'
                }
              </p>
            </div>
            <div className="flex items-center gap-x-4 mt-4 lg:mt-0">
              <button className={`${theme.primary} px-6 py-3 rounded-xl font-semibold ${theme.primaryHover} transition-all duration-200 shadow-lg`}>
                <Zap className="w-5 h-5 inline mr-2" />
                Go Live
              </button>
              {!isAuthenticated && (
                <button
                  onClick={() => navigate('/signup')}
                  className={`border-2 ${theme.border} ${theme.text} px-6 py-3 rounded-xl font-semibold ${theme.cardHover} transition-all duration-200`}
                >
                  Join Now
                </button>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {quickStats.map((stat, index) => (
              <div key={index} className={`${theme.card} rounded-xl ${theme.shadow} p-4`}>
                <div className="flex items-center justify-between mb-2">
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                    stat.change.includes('+') ? 'bg-green-100 text-green-600' :
                    stat.change.includes('-') ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {stat.change}
                  </span>
                </div>
                <div className={`text-2xl font-bold ${theme.text} mb-1`}>{stat.value}</div>
                <div className={`text-sm ${theme.textSecondary}`}>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Main Grid */}
          <div className="grid lg:grid-cols-3 gap-8">

            {/* Left Column - Featured Matches */}
            <div className="lg:col-span-2 space-y-6">

              {/* Live Matches */}
              <div className={`${theme.card} rounded-xl ${theme.shadow} p-6`}>
                <div className="flex items-center justify-between mb-6">
                  <h2 className={`text-2xl font-bold ${theme.text} flex items-center`}>
                    <PlayCircle className="w-6 h-6 mr-2 text-red-500" />
                    Live & Upcoming
                  </h2>
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-gray-400" />
                    <select
                      value={activeFilter}
                      onChange={(e) => setActiveFilter(e.target.value)}
                      className={`${theme.backgroundSecondary} ${theme.text} text-sm rounded-lg px-3 py-1 border-none focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value="all">All Sports</option>
                      <option value="basketball">Basketball</option>
                      <option value="volleyball">Volleyball</option>
                      <option value="pickleball">Pickleball</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-4">
                  {featuredMatches.map((match) => (
                    <div
                      key={match.id}
                      className={`${theme.backgroundSecondary} rounded-xl p-5 ${theme.cardHover} cursor-pointer transition-all duration-200 hover:scale-[1.02]`}
                      onClick={() => navigate('/tournaments')}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-x-3">
                          <span className="text-2xl">{match.emoji}</span>
                          <div>
                            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                              match.status === 'Live' ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-blue-100 text-blue-600'
                            }`}>
                              {match.status}
                            </span>
                            <p className={`text-sm ${theme.textMuted} mt-1`}>{match.time}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center text-sm text-gray-500">
                            <Users className="w-4 h-4 mr-1" />
                            {match.viewers} watching
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-center gap-x-6">
                        <div className="text-center flex-1">
                          <div className={`text-lg font-semibold ${theme.text}`}>{match.teams[0]}</div>
                          <div className="text-3xl font-bold text-blue-600 mt-1">{match.score[0]}</div>
                        </div>
                        <div className={`text-2xl font-bold ${theme.textMuted}`}>vs</div>
                        <div className="text-center flex-1">
                          <div className={`text-lg font-semibold ${theme.text}`}>{match.teams[1]}</div>
                          <div className="text-3xl font-bold text-red-600 mt-1">{match.score[1]}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Upcoming Events */}
              <div className={`${theme.card} rounded-xl ${theme.shadow} p-6`}>
                <div className="flex items-center justify-between mb-6">
                  <h2 className={`text-2xl font-bold ${theme.text} flex items-center`}>
                    <Calendar className="w-6 h-6 mr-2 text-purple-500" />
                    Major Events
                  </h2>
                  <button
                    onClick={() => navigate('/tournaments')}
                    className={`text-sm ${theme.primary} font-semibold hover:underline flex items-center`}
                  >
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </button>
                </div>

                <div className="grid gap-4">
                  {upcomingEvents.map((event) => (
                    <div
                      key={event.id}
                      className={`${theme.backgroundSecondary} rounded-lg p-4 ${theme.cardHover} cursor-pointer transition-all duration-200 border-l-4 border-purple-500`}
                      onClick={() => navigate('/tournaments')}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className={`text-lg font-semibold ${theme.text} mb-2`}>{event.title}</h3>
                          <div className="flex items-center gap-x-4 text-sm">
                            <div className="flex items-center">
                              <Clock className="w-4 h-4 mr-1 text-blue-500" />
                              <span className={theme.textSecondary}>{event.date} • {event.time}</span>
                            </div>
                            <div className="flex items-center">
                              <MapPin className="w-4 h-4 mr-1 text-green-500" />
                              <span className={theme.textSecondary}>{event.location}</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-lg font-bold text-green-600 mb-1">{event.prize}</div>
                          <div className={`text-sm ${theme.textMuted}`}>{event.participants} players</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Leaderboard & Quick Actions */}
            <div className="space-y-6">

              {/* Global Leaderboard */}
              <div className={`${theme.card} rounded-xl ${theme.shadow} p-6`}>
                <div className="flex items-center justify-between mb-6">
                  <h2 className={`text-xl font-bold ${theme.text} flex items-center`}>
                    <TrendingUp className="w-5 h-5 mr-2 text-orange-500" />
                    Global Leaders
                  </h2>
                  <button className={`text-sm ${theme.primary} font-semibold hover:underline`}>
                    View Full Rankings
                  </button>
                </div>

                <div className="space-y-3">
                  {leaderboard.map((player) => (
                    <div key={player.rank} className={`flex items-center justify-between p-3 ${theme.backgroundSecondary} rounded-lg`}>
                      <div className="flex items-center gap-x-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          player.rank === 1 ? 'bg-yellow-100 text-yellow-600' :
                          player.rank === 2 ? 'bg-gray-100 text-gray-600' :
                          player.rank === 3 ? 'bg-orange-100 text-orange-600' :
                          theme.backgroundSecondary
                        }`}>
                          {player.rank === 1 ? '👑' : `#${player.rank}`}
                        </div>
                        <div>
                          <div className={`font-semibold ${theme.text}`}>{player.name}</div>
                          <div className={`text-xs ${theme.textMuted}`}>{player.sport}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`font-semibold ${theme.text}`}>{player.points}</div>
                        <div className={`text-xs ${player.trend.startsWith('+') ? 'text-green-500' : 'text-red-500'}`}>
                          {player.trend}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Actions */}
              <div className={`${theme.card} rounded-xl ${theme.shadow} p-6`}>
                <h2 className={`text-xl font-bold ${theme.text} mb-6 flex items-center`}>
                  <Zap className="w-5 h-5 mr-2 text-blue-500" />
                  Quick Actions
                </h2>

                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/tournaments')}
                    className={`w-full ${theme.backgroundSecondary} ${theme.cardHover} rounded-lg p-4 text-left transition-all duration-200 hover:scale-105`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className={`font-semibold ${theme.text}`}>Join Tournament</div>
                        <div className={`text-sm ${theme.textMuted}`}>23 active tournaments</div>
                      </div>
                      <Trophy className="w-6 h-6 text-yellow-500" />
                    </div>
                  </button>

                  <button
                    onClick={() => navigate('/social-feed')}
                    className={`w-full ${theme.backgroundSecondary} ${theme.cardHover} rounded-lg p-4 text-left transition-all duration-200 hover:scale-105`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className={`font-semibold ${theme.text}`}>Social Feed</div>
                        <div className={`text-sm ${theme.textMuted}`}>Connect with players</div>
                      </div>
                      <Users className="w-6 h-6 text-green-500" />
                    </div>
                  </button>

                  <button
                    onClick={() => navigate('/marketplace')}
                    className={`w-full ${theme.backgroundSecondary} ${theme.cardHover} rounded-lg p-4 text-left transition-all duration-200 hover:scale-105`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className={`font-semibold ${theme.text}`}>Shop Gear</div>
                        <div className={`text-sm ${theme.textMuted}`}>Latest sports equipment</div>
                      </div>
                      <Star className="w-6 h-6 text-purple-500" />
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}